/**
 * Frame classification: what kind of card is in front of the camera, before any
 * text is read.
 *
 * Stage 1 of the plan's pipeline, and it exists for one reason it states: *you
 * cannot OCR a footer whose position you don't know*. The footer template depends
 * on the era (a 1993 card prints no collector number at all, an M15 card prints it
 * in a black band, a 2023 card prints rarity before a four-digit number), and the
 * crop depends on the treatment (borderless and full-art move the landmarks a
 * fixed crop would anchor to).
 *
 * Pass 4 measured this classifier against real Scryfall cards and found it broken:
 * on the fine-print era it was right 0 of 40, because its art-bottom rule (the last
 * row whose neighbourhood is more than 45% structured) counts the type box's rules
 * text as picture and reported an art bottom of 0.86-0.89 where the picture ends
 * near 0.54. The measurement that replaced it is in `art-window.ts`, and what it
 * found about the *frames* is what this module now encodes:
 *
 *  - **The printed art window barely moves between frames.** Alpha, Tempest,
 *    Eighth Edition, Innistrad and Magic 2015 all put the picture at 54-55% of the
 *    card with the same height. The old rule that an early frame's picture "ends
 *    around 62%" was never true of a real card.
 *  - **What does move is the picture's vertical position and the printed margin
 *    beside it.** The frames that print no collector number (Alpha .. Eighth
 *    Edition, 1993-2003) sit the picture marginally higher than the frames from
 *    Mirrodin (October 2003) to Magic 2014, and the Magic 2015 frame - the first
 *    with a band - widens the picture by about 2% of the card on each side.
 *  - **That is a weak signal, and the classifier says so.** The two families are
 *    1.2% of the card apart in the picture's bottom edge, which is about five
 *    pixels on a 420-pixel capture. It is enough to order the candidates and not
 *    enough to decide anything alone, which is why the era the footer *prints* still
 *    overrides this classification (see `footer.ts`).
 *
 * The treatment question is different, and geometry does settle it: a picture that
 * runs on past 76% of the card is full-art, and one whose structure reaches the
 * capture's own edges is borderless. Those two facts decide which region the footer
 * template reads from, and the borders of the card decide nothing.
 */
import { locateArtWindow, profileFrame } from './art-window.js';
/** Card aspect we are willing to treat as a card: 2.5x3.5 is 0.714. */
const ASPECT_MIN = 0.6;
const ASPECT_MAX = 0.88;
/** A pixel is "structured" when its neighbour differs by this much. */
const GRADIENT = 6;
/** A row this uniform and this dark at the foot of the card is a printed band. */
const BAND_LUMA = 82;
const BAND_UNIFORM = 0.3;
/** The shortest run of such rows that is a printed band rather than the card's edge. */
const BAND_MIN_HEIGHT = 0.06;
/**
 * Per-pixel differences this large across the picture are not a card.
 *
 * A printed picture, even photographed, is smooth next to sensor noise: adjacent
 * pixels in a real capture differ by a handful of levels, while noise differs by
 * tens. This is the one guard that keeps the classifier from confidently calling
 * a frame full of noise "borderless".
 */
const MAX_ART_GRADIENT = 45;
/**
 * Where the picture ends, measured across real frames: 0.540 on the frames from
 * Alpha to Eighth Edition, 0.552 on those from Mirrodin to Magic 2014, and 0.550 on
 * the Magic 2015 frame. The split sits between the two pre-band families.
 *
 * 0.536 rather than the printed 0.546, because the split is a threshold on the
 * *classifier's* measurement, not on the card: `locateArtWindow` reports a bottom
 * 1.0% of the card above the printed edge (see `ART_BOTTOM_BIAS`), and a threshold
 * quoted in printed coordinates would put every card on the wrong side of it. This
 * is the kind of thing the benchmark exists to catch - it did, at 0/12 on the fine
 * print era, and this is the correction.
 */
export const PREBAND_ART_BOTTOM_SPLIT = 0.536;
const PREBAND_ART_BOTTOM_SPREAD = 0.012;
/** Past this, the picture is a full-art or borderless treatment. */
const FULL_ART_ART_BOTTOM = 0.76;
const round3 = (value) => Math.round(value * 1000) / 1000;
function validate(frame) {
    if (!Number.isInteger(frame.width) || !Number.isInteger(frame.height) || frame.width < 24 || frame.height < 32) {
        throw new Error(`a card frame is too small to classify at ${frame.width}x${frame.height}: the device should send at least 24x32`);
    }
    if (frame.levels.length !== frame.width * frame.height) {
        throw new Error(`frame says ${frame.width}x${frame.height} but holds ${frame.levels.length} levels, not ${frame.width * frame.height}`);
    }
}
/** Mean |left - right| and the structured fraction of one row, over a column span. */
function rowProfile(frame, y, x0, x1) {
    const start = Math.max(0, Math.floor(x0));
    const end = Math.min(frame.width - 1, Math.ceil(x1));
    if (end <= start)
        return { gradient: 0, busy: 0 };
    let total = 0;
    let structured = 0;
    for (let x = start; x < end; x += 1) {
        const difference = Math.abs((frame.levels[y * frame.width + x] ?? 0) - (frame.levels[y * frame.width + x + 1] ?? 0));
        total += difference;
        if (difference > GRADIENT)
            structured += 1;
    }
    const samples = end - start;
    return { gradient: total / samples, busy: structured / samples };
}
const middleBand = (frame) => [frame.width * 0.1, frame.width * 0.9];
/** The printed band at the foot of the card: a run of dark, uniform, tall rows. */
function findBand(frame, luma, busy, half) {
    const rows = frame.height;
    let top = null;
    let bottom = null;
    for (let y = Math.round(rows * 0.7); y < rows; y += 1) {
        const dark = (luma[y] ?? 255) < BAND_LUMA;
        const uniform = (busy[y] ?? 1) < BAND_UNIFORM;
        if (dark && uniform) {
            if (top === null)
                top = y;
            bottom = y;
        }
        else if (top !== null && y - (bottom ?? y) > half * 2) {
            break;
        }
    }
    if (top !== null && bottom !== null && (bottom - top + 1) / rows < BAND_MIN_HEIGHT)
        return { top: null, bottom: null };
    return { top, bottom };
}
/** Mean of a row statistic over the card's rows between two fractions of height. */
function meanOver(profile, from, to, pick) {
    const start = Math.max(0, Math.round(from * profile.rows.length));
    const end = Math.min(profile.rows.length, Math.round(to * profile.rows.length));
    if (end <= start)
        return 0;
    let total = 0;
    for (let index = start; index < end; index += 1)
        total += pick(profile.rows[index]);
    return total / (end - start);
}
export function measureFrame(frame, context) {
    validate(frame);
    const [x0, x1] = middleBand(frame);
    const aspect = frame.width / frame.height;
    const rows = frame.height;
    const measured = context ?? frameContext(frame);
    const profile = measured.profile;
    const window = measured.window;
    const busy = [];
    const gradient = [];
    const luma = [];
    for (let y = 0; y < rows; y += 1) {
        const stats = profile.rows[y];
        busy.push(stats.busy);
        gradient.push(stats.gradient);
        let sum = 0;
        for (let x = Math.floor(x0); x < Math.floor(x1); x += 1)
            sum += frame.levels[y * frame.width + x] ?? 0;
        luma.push(sum / Math.max(1, Math.floor(x1) - Math.floor(x0)));
    }
    const half = Math.max(1, Math.round(0.02 * rows));
    const band = findBand(frame, luma, busy, half);
    const artBottom = window.rect.y1;
    const artTop = window.rect.y0;
    // The margin: how far in a uniform ring extends from the card's edge.
    let marginWidth = 0;
    for (let inset = 1; inset <= Math.round(frame.width * 0.09); inset += 1) {
        let sum = 0;
        let samples = 0;
        for (let y = inset; y < rows - inset; y += Math.max(1, Math.round(rows / 24))) {
            sum += Math.abs((frame.levels[y * frame.width + inset] ?? 0) - (frame.levels[y * frame.width + inset + 1] ?? 0)) > GRADIENT ? 1 : 0;
            sum += Math.abs((frame.levels[y * frame.width + (frame.width - 1 - inset)] ?? 0) - (frame.levels[y * frame.width + (frame.width - 2 - inset)] ?? 0)) > GRADIENT ? 1 : 0;
            samples += 2;
        }
        const structured = sum / Math.max(1, samples);
        if (structured > 0.25)
            break;
        marginWidth = inset / frame.width;
    }
    // Does the picture reach the edges? Sampled *inside* the margin, so a
    // borderless printing's printed edge cannot be mistaken for frame colour.
    const edgeSpan = Math.max(1, Math.round(Math.min(frame.width, frame.height) * 0.02));
    const marginPixels = Math.max(1, Math.round(marginWidth * frame.width));
    const spanTop = Math.round(rows * 0.2);
    const spanBottom = Math.max(spanTop + 1, Math.round(artBottom * rows) - Math.round(rows * 0.02));
    let edgeSum = 0;
    let edgeCount = 0;
    for (let y = spanTop; y < spanBottom; y += Math.max(1, Math.round(rows / 40))) {
        const left = rowProfile(frame, y, marginPixels + 1, marginPixels + 1 + edgeSpan);
        const right = rowProfile(frame, y, frame.width - marginPixels - 2 - edgeSpan, frame.width - marginPixels - 2);
        edgeSum += left.busy + right.busy;
        edgeCount += 2;
    }
    const bottomFrom = Math.round(rows * 0.875);
    let bottomSum = 0;
    for (let y = bottomFrom; y < rows; y += 1)
        bottomSum += luma[y] ?? 0;
    // Structure inside the picture itself: what tells a card from sensor noise.
    const artSpanEnd = Math.max(Math.round(artTop * rows) + 1, Math.round(artBottom * rows));
    let gradientSum = 0;
    let gradientRows = 0;
    for (let y = Math.round(artTop * rows); y < artSpanEnd; y += 1) {
        gradientSum += gradient[y] ?? 0;
        gradientRows += 1;
    }
    return {
        aspect: round3(aspect),
        artBottom: round3(artBottom),
        artTop: round3(artTop),
        artLeft: round3(window.rect.x0),
        artRight: round3(window.rect.x1),
        pictureHeight: round3(window.rect.y1 - window.rect.y0),
        bandTop: band.top === null ? null : round3(band.top / rows),
        bandHeight: band.top === null || band.bottom === null ? 0 : round3((band.bottom - band.top + 1) / rows),
        marginWidth: round3(marginWidth),
        edgeBusy: round3(edgeSum / Math.max(1, edgeCount)),
        bottomLuma: round3(bottomSum / Math.max(1, rows - bottomFrom) / 255),
        artGradient: round3(gradientSum / Math.max(1, gradientRows)),
        textStdBelow: round3(meanOver(profile, artBottom, Math.min(1, artBottom + 0.1), (row) => row.std)),
        textPaperBelow: round3(meanOver(profile, artBottom, Math.min(1, artBottom + 0.1), (row) => row.paper)),
        windowConfidence: round3(window.confidence),
    };
}
export function frameContext(frame) {
    validate(frame);
    const profile = profileFrame(frame);
    return { profile, window: locateArtWindow(frame, { profile }) };
}
/** How far past a threshold a measurement sits, as a confidence in [0.5, 0.98]. */
function confidenceFrom(distance, scale) {
    return Math.min(0.98, Math.max(0.5, 0.5 + (distance / scale) * 0.48));
}
export function classifyFrame(frame, context) {
    const measured = context ?? frameContext(frame);
    const signals = measureFrame(frame, measured);
    const candidates = [];
    const notes = [];
    const profile = measured.profile;
    const window = measured.window;
    const windowRect = window.rect;
    const notACard = signals.aspect < ASPECT_MIN || signals.aspect > ASPECT_MAX;
    if (notACard) {
        notes.push(`the frame is ${signals.aspect} wide for its height, not the 0.716 a card is: the capture guide is probably off the card`);
        return {
            frameClass: 'unknown',
            treatment: 'standard',
            confidence: 0.3,
            signals,
            window: windowRect,
            candidates: [{ frameClass: 'unknown', confidence: 0.3, why: 'the aspect ratio is not a card' }],
            notes,
        };
    }
    const notAPicture = signals.artGradient > MAX_ART_GRADIENT;
    if (notAPicture) {
        notes.push(`adjacent pixels differ by ${signals.artGradient} levels across the frame: that is sensor noise or a subject that is not a card, not a printed picture`);
        return {
            frameClass: 'unknown',
            treatment: 'standard',
            confidence: 0.3,
            signals,
            window: windowRect,
            candidates: [{ frameClass: 'unknown', confidence: 0.3, why: 'the frame does not look like a printed picture' }],
            notes,
        };
    }
    const band = signals.bandTop !== null && signals.bandHeight >= BAND_MIN_HEIGHT;
    const fullArt = signals.artBottom >= FULL_ART_ART_BOTTOM;
    const borderless = !band && signals.marginWidth < 0.025 && signals.edgeBusy > 0.3 && signals.artBottom > 0.6;
    notes.push(...window.evidence);
    if (window.extended) {
        notes.push(`the picture covers ${signals.pictureHeight} of the card height (${signals.artTop} to ${signals.artBottom}): a full-art or extended-art treatment`);
    }
    if (borderless) {
        candidates.push({
            frameClass: 'borderless',
            confidence: confidenceFrom(0.025 - signals.marginWidth, 0.025),
            why: `the picture reaches the card's edges (margin ${signals.marginWidth}, edge structure ${signals.edgeBusy})`,
        });
        notes.push('a borderless or full-art printing: the footer landmarks are gone, so the template is the whole lower card rather than a framed band');
    }
    else if (band) {
        // No attempt to split the two band eras by position. They sit within a
        // percent or two of each other on the card, and the difference that matters -
        // `n rarity setcode lang` against `rarity setcode nnnn lang` - is in the text.
        // The 2014 era is the prior because it is eleven of those years.
        candidates.push({
            frameClass: 'm15_2014',
            confidence: 0.62,
            why: `a printed band starting at ${signals.bandTop} of the card height, ${signals.bandHeight} tall`,
        });
        candidates.push({
            frameClass: 'mom_2023',
            confidence: 0.5,
            why: 'the same band; the four-digit, rarity-first text layout is what distinguishes it',
        });
        notes.push('M15 (2014) and March of the Machine (2023) print the same band within a percent of each other, so geometry cannot split them: the footer text decides which era this is');
    }
    else {
        // The pre-band era question, measured rather than assumed. The frames that
        // print no collector number (Alpha .. Eighth Edition) put the picture about
        // 1.2% of the card higher than the frames from Mirrodin to Magic 2014, and the
        // Magic 2015 frame - which has a band, so it is not here - widens it by 2% of
        // the card on each side. The wide margin is a second, independent witness of
        // the same split.
        const oldFrame = signals.artBottom < PREBAND_ART_BOTTOM_SPLIT;
        const inset = Math.min(signals.artLeft, 1 - signals.artRight);
        const widePanel = inset >= 0.1;
        const agreement = oldFrame === widePanel ? 1 : 0;
        const primary = oldFrame ? 'footer_1993' : 'footer_2003';
        const secondary = oldFrame ? 'footer_2003' : 'footer_1993';
        const distance = Math.abs(signals.artBottom - PREBAND_ART_BOTTOM_SPLIT);
        candidates.push({
            frameClass: primary,
            confidence: confidenceFrom(distance + (agreement === 1 ? PREBAND_ART_BOTTOM_SPREAD : 0), PREBAND_ART_BOTTOM_SPREAD * 3),
            why: `the picture ends at ${signals.artBottom} of the card height with no printed band ` +
                `(the frames that print no collector number end at 0.540, the ones from Mirrodin at 0.552)`,
        });
        candidates.push({
            frameClass: secondary,
            confidence: agreement === 1 ? 0.4 : 0.47,
            why: 'the other pre-band family; whether a collector number exists at all is a fact about the text, not the frame',
        });
        notes.push('the two pre-band families are 1.2% of the card apart in the picture\'s bottom edge, which orders the candidates without deciding them: if the footer parses as number/total the card is 2003 or later, and if it carries no number it is pre-Exodus');
        if (agreement === 0) {
            notes.push(`the two witnesses disagree (the picture ends at ${signals.artBottom}, and the printed margin beside it is ${inset} wide, which belongs to the other family), so this ordering is a coin toss and the footer text has to settle it`);
        }
    }
    // Treatment comes from the measured picture, not from the era: how much of the
    // card it covers, and whether a printed band is still there to anchor the footer.
    // A full-art printing keeps its band; a borderless one has nothing to anchor to,
    // which is exactly the distinction the footer template needs.
    const treatment = borderless
        ? 'borderless'
        : fullArt
            ? 'full_art'
            : 'standard';
    if (treatment === 'full_art') {
        notes.push(`the picture covers ${signals.pictureHeight} of the card height but the frame still prints a band: full-art or extended-art`);
    }
    if (treatment === 'borderless' && fullArt) {
        notes.push('a borderless and an extended-art printing have the same picture geometry - the art to the edges, no band - so the cascade treats both as treatment-moved and reads the footer from the lower card');
    }
    const typeLine = meanOver(profile, Math.min(0.98, signals.artBottom), Math.min(1, signals.artBottom + 0.04), (row) => row.busy);
    if (typeLine > 0.8 && !fullArt) {
        notes.push(`the type line under the picture is ${typeLine.toFixed(2)} structured, which is why the picture is measured to end at ${signals.artBottom}`);
    }
    const winner = candidates[0];
    const ordered = [...candidates].sort((a, b) => b.confidence - a.confidence);
    return {
        frameClass: ordered[0].frameClass,
        treatment,
        confidence: winner.confidence,
        signals,
        window: windowRect,
        candidates: ordered,
        notes,
    };
}
//# sourceMappingURL=frame.js.map